/**
 * Shared extraction-status utilities.
 *
 * Pulled out of ExtractionPage so the chat surface (ChatUploadCard) can
 * render the same progress visual without duplicating the config map,
 * cycling-message hook, or stage builder. Framework §7.5.
 */
import type { ProgressStage } from '../../../components/ui/ProgressBar';

export interface ExtractionStatusConfig {
  label: string;
  color: 'success' | 'warning' | 'error' | 'secondary';
  progress: number;
  message: string;
}

export const STATUS_CONFIG: Record<string, ExtractionStatusConfig> = {
  UPLOADED: {
    label: 'Uploaded',
    color: 'secondary',
    progress: 5,
    message: 'Document received. Waiting to process...',
  },
  PROCESSING: {
    label: 'Processing',
    color: 'warning',
    progress: 40,
    message: 'Extracting key terms from document...',
  },
  EXTRACTED: {
    label: 'Extracted',
    color: 'warning',
    progress: 60,
    message: 'Initial extraction complete. Running validation...',
  },
  VALIDATING: {
    label: 'Validating',
    color: 'warning',
    progress: 70,
    message: 'Validating extracted fields...',
  },
  VALIDATED: {
    label: 'Validated',
    color: 'warning',
    progress: 80,
    message: 'Validation complete. Running analysis...',
  },
  ANALYZING: {
    label: 'Analyzing',
    color: 'warning',
    progress: 90,
    message: 'Scoring and benchmarking your term sheet...',
  },
  ANALYZED: {
    label: 'Complete',
    color: 'success',
    progress: 100,
    message: 'Extraction complete. Ready for analysis.',
  },
  ERROR: {
    label: 'Failed',
    color: 'error',
    progress: 0,
    message: 'Extraction failed. Try again.',
  },
};

export const COMPLETE_STATUSES = new Set(['ANALYZED', 'COMPLETE', 'completed']);
export const FAILED_STATUSES = new Set(['ERROR', 'failed', 'not_term_sheet']);

export function getStatusConfig(status: string | null | undefined): ExtractionStatusConfig {
  if (!status) {return STATUS_CONFIG.UPLOADED;}
  return STATUS_CONFIG[status] ?? STATUS_CONFIG.UPLOADED;
}

export function getProgressStages(status: string | null | undefined): ProgressStage[] {
  const config = getStatusConfig(status);
  return [
    { label: 'Uploaded', completed: config.progress > 5 },
    { label: 'Processing', completed: config.progress > 40, active: status === 'PROCESSING' },
    { label: 'Extracted', completed: config.progress > 60, active: status === 'EXTRACTED' },
    {
      label: 'Validated',
      completed: config.progress > 80,
      active: status === 'VALIDATING' || status === 'VALIDATED',
    },
    { label: 'Complete', completed: config.progress >= 100, active: status === 'ANALYZING' },
  ];
}
